import { jsPDF } from "jspdf";
import { formatDate, formatWeekRange } from "../utils/dateUtils";
import { cleanTextForPDF } from "../utils/textUtils";

/**
 * Helper function to check if we need a new page and add it if necessary
 * @param {Object} doc - jsPDF document instance
 * @param {number} yPos - Current Y position
 * @param {number} pageHeight - Page height
 * @param {number} requiredSpace - Space needed in mm
 * @param {number} topMargin - Top margin for new page
 * @returns {number} Updated Y position
 */
function ensurePageSpace(doc, yPos, pageHeight, requiredSpace = 20, topMargin = 10) {
  const bottomMargin = 15; // Reserve 15mm at bottom
  if (yPos + requiredSpace > pageHeight - bottomMargin) {
    doc.addPage();
    return topMargin;
  }
  return yPos;
}

/**
 * Helper function to render a task and its subtasks with proper pagination (for daily view)
 * @param {Object} doc - jsPDF document instance
 * @param {Object} task - Task object with potential subitems
 * @param {number} xPos - X position for text
 * @param {number} yPos - Current Y position (will be updated)
 * @param {number} pageHeight - Page height for pagination checks
 * @param {number} maxWidth - Maximum text width
 * @param {number} lineHeight - Line height spacing
 * @param {number} indentLevel - Indentation level (0 for main task, 1+ for subtasks)
 * @param {number} minYPos - Minimum Y position before adding new page
 * @returns {number} Updated Y position
 */
function renderTaskWithSubtasks(doc, task, xPos, yPos, pageHeight, maxWidth, lineHeight, indentLevel = 0, minYPos = 10) {
  const indent = indentLevel * 4; // 4mm indent per level
  const taskIndent = indent + 2; // Base indent + task indent
  
  // Show task name with dot prefix
  let taskName = String(task.name || "");
  taskName = cleanTextForPDF(taskName);
  
  // Add dot before task name
  const taskTextWithDot = `• ${taskName}`;
  
  doc.setFont("helvetica", "normal");
  doc.setCharSpace(0);
  
  // Calculate available width for text (accounting for indent)
  const availableWidth = maxWidth - taskIndent;
  const lines = doc.splitTextToSize(taskTextWithDot, availableWidth);
  
  // Calculate total height needed for this task (including all lines)
  const taskHeight = lines.length * lineHeight;
  
  // Check if we need a new page before rendering task
  // Reserve space for the task and potential subtasks
  yPos = ensurePageSpace(doc, yPos, pageHeight, taskHeight + 20, minYPos);
  
  // Render each line of the task
  lines.forEach((line, idx) => {
    // Check before each line (safety check)
    yPos = ensurePageSpace(doc, yPos, pageHeight, lineHeight, minYPos);
    
    let cleanLine = String(line || "");
    cleanLine = cleanTextForPDF(cleanLine);
    const textX = xPos + taskIndent;
    
    // Draw text at current yPos (not offset by index)
    doc.text(cleanLine, textX, yPos);
    
    // Move to next line
    yPos += lineHeight;
  });
  
  // Render subtasks if they exist
  if (task.subitems && Array.isArray(task.subitems) && task.subitems.length > 0) {
    // Filter out completed subtasks
    const uncompletedSubtasks = task.subitems.filter((subtask) => {
      // Subtasks might have a done property or status
      return !subtask.done && !subtask.isCompletedEntry;
    });
    
    uncompletedSubtasks.forEach((subtask) => {
      // Check if we need a new page before rendering subtask
      yPos = ensurePageSpace(doc, yPos, pageHeight, 15, minYPos);
      
      // Recursively render subtask (with increased indent level)
      yPos = renderTaskWithSubtasks(
        doc,
        subtask,
        xPos,
        yPos,
        pageHeight,
        maxWidth,
        lineHeight,
        indentLevel + 1,
        minYPos
      );
    });
  }
  
  return yPos;
}

/**
 * Helper function to render a task and its subtasks in weekly view (column layout)
 * @param {Object} doc - jsPDF document instance
 * @param {Object} task - Task object with potential subitems
 * @param {number} xPos - X position for column
 * @param {number} yPos - Current Y position (will be updated)
 * @param {number} columnWidth - Column width
 * @param {number} columnPadding - Column padding
 * @param {number} startY - Start Y position for the column
 * @param {number} maxContentHeight - Maximum content height for the column
 * @param {number} lineHeight - Line height spacing
 * @param {number} indentLevel - Indentation level (0 for main task, 1+ for subtasks)
 * @returns {number} Updated Y position, or null if content doesn't fit
 */
function renderTaskWithSubtasksWeekly(doc, task, xPos, yPos, columnWidth, columnPadding, startY, maxContentHeight, lineHeight, indentLevel = 0) {
  const indent = indentLevel * 3; // 3mm indent per level for weekly view
  const taskIndent = indent + columnPadding + 2; // Base indent + task indent
  
  // Check if we have enough space
  if (yPos > startY + maxContentHeight - 8) {
    return null; // Signal that content doesn't fit
  }
  
  // Show task name with dot prefix
  let taskName = String(task.name || "");
  taskName = cleanTextForPDF(taskName);
  
  // Add dot before task name
  const taskTextWithDot = `• ${taskName}`;
  
  doc.setFont("helvetica", "normal");
  doc.setCharSpace(0);
  
  // Calculate available width for text (accounting for indent and safety margin)
  const maxTextWidth = Math.max(6, columnWidth - taskIndent - 4);
  const lines = doc.splitTextToSize(taskTextWithDot, maxTextWidth);
  
  // Render each line of the task
  lines.forEach((line) => {
    // Check if we have enough space for this line
    if (yPos > startY + maxContentHeight - 5) {
      return; // Stop rendering if no space
    }
    
    let cleanLine = String(line || "");
    cleanLine = cleanTextForPDF(cleanLine);
    const textX = xPos + taskIndent;
    const maxX = xPos + columnWidth - columnPadding - 1;
    
    // Strict boundary check - ensure text doesn't exceed column
    if (textX + doc.getTextWidth(cleanLine) <= maxX) {
      doc.text(cleanLine, textX, yPos);
    } else {
      // If line would exceed, truncate
      const truncated = cleanLine.substring(0, Math.floor(cleanLine.length * 0.8));
      if (textX + doc.getTextWidth(truncated) <= maxX) {
        doc.text(truncated + "...", textX, yPos);
      }
    }
    yPos += lineHeight;
  });
  
  // Render subtasks if they exist
  if (task.subitems && Array.isArray(task.subitems) && task.subitems.length > 0) {
    // Filter out completed subtasks
    const uncompletedSubtasks = task.subitems.filter((subtask) => {
      return !subtask.done && !subtask.isCompletedEntry;
    });
    
    uncompletedSubtasks.forEach((subtask) => {
      // Recursively render subtask (with increased indent level)
      const newYPos = renderTaskWithSubtasksWeekly(
        doc,
        subtask,
        xPos,
        yPos,
        columnWidth,
        columnPadding,
        startY,
        maxContentHeight,
        lineHeight,
        indentLevel + 1
      );
      
      if (newYPos !== null) {
        yPos = newYPos;
      } else {
        // Content doesn't fit, stop rendering
        return;
      }
    });
  }
  
  return yPos;
}

export function exportPDF({
  viewType,
  currentDate,
  currentWeekStart,
  tasksByProperty,
  selectedPerson,
  availablePeople,
  propertyNameToIdMap,
  propertyKeyMap,
}) {
  // Use landscape orientation
  const doc = new jsPDF({ orientation: "landscape", unit: "mm" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 6; // Increased from 3, but still minimal
  const lineHeight = 4; // Keep line spacing the same
  
  // Set character spacing to 0 globally to prevent letter spacing issues
  doc.setCharSpace(0);

  if (viewType === "daily") {
    // Daily view: portrait or landscape with properties
    let yPos = 10; // Added top padding
    const maxWidth = pageWidth - 2 * margin;
    const lineHeight = 5; // Reduced from 9

    // Header
    doc.setFontSize(18); // Increased from 14
    doc.setFont("helvetica", "bold");
    doc.text(`Tasks - ${formatDate(currentDate)}`, margin, yPos);
    yPos += 6; // Keep line spacing the same

    // Person filter info
    if (selectedPerson !== "all") {
      const personName = availablePeople.find(p => p.id === selectedPerson)?.name || selectedPerson;
      doc.setFontSize(13); // Increased from 10
      doc.setFont("helvetica", "normal");
      doc.text(`Filtered by: ${cleanTextForPDF(personName)}`, margin, yPos);
      yPos += 5; // Reduced from 10
    }

    // Daily view: grouped by property
    const propertyNames = Object.keys(tasksByProperty).sort((a, b) => a.localeCompare(b));
    
    propertyNames.forEach((propertyName) => {
      const tasks = Array.isArray(tasksByProperty[propertyName]) 
        ? tasksByProperty[propertyName] 
        : [];
      
      if (tasks.length === 0) return;

      // Check if we need a new page before property header
      // Reserve space for property header (5mm) and at least one task with potential subtasks (40mm)
      yPos = ensurePageSpace(doc, yPos, pageHeight, 45, 10);

      // Add spacing before property (except first)
      if (propertyNames.indexOf(propertyName) > 0) {
        yPos += 2; // Reduced from 6
      }

      // Property header with Key #
      doc.setFontSize(14); // Increased from 11
      doc.setFont("helvetica", "bold");
      const propId = propertyNameToIdMap[propertyName];
      const keyValue = propId ? (propertyKeyMap[propId] || propertyKeyMap[String(propId)]) : null;
      const propertyDisplayName = keyValue ? `${propertyName} (${keyValue})` : propertyName;
      doc.text(cleanTextForPDF(propertyDisplayName), margin, yPos);
      yPos += 5; // Keep line spacing the same

      // Tasks - filter out completed tasks
      const uncompletedTasks = tasks.filter((task) => !task.done && !task.isCompletedEntry);
      
      doc.setFontSize(11); // Increased from 9
      doc.setFont("helvetica", "normal");
      uncompletedTasks.forEach((task, taskIndex) => {
        // Check if we need a new page before rendering task
        // The renderTaskWithSubtasks function will handle its own pagination, but we check here too
        yPos = ensurePageSpace(doc, yPos, pageHeight, 20, 10);

        // Add minimal spacing between tasks - match property spacing
        if (taskIndex > 0) {
          yPos += 0; // No extra spacing between tasks
        }

        // Use helper function to render task with subtasks
        yPos = renderTaskWithSubtasks(
          doc,
          task,
          margin,
          yPos,
          pageHeight,
          maxWidth,
          lineHeight,
          0, // No indent for main tasks
          10 // Minimum Y position
        );
      });
      yPos += 3; // Reduced from 10 - Extra spacing after property group
    });
  } else {
    // Weekly view: landscape with days side by side
    const dayOrder = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const columnSpacing = 1; // Reduced from 4
    const columnPadding = 1; // Reduced from 4
    const columnWidth = ((pageWidth - 2 * margin) - (6 * columnSpacing)) / 7; // 6 gaps between 7 columns
    const startY = 17; // Added top padding (was 12)
    const maxContentHeight = pageHeight - startY - 5; // Reduced from 15
    const bottomMargin = 15; // Reserve space at bottom

    // Helper function to render a single day's content with pagination support
    const renderDayContent = (day, dayIndex, xPos, currentPageStartY, pageNum, renderedTaskIds = {}) => {
      const dayData = tasksByProperty[day];
      let dayY = currentPageStartY;
      let hasMoreContent = false;
      let renderedPropertyCount = 0;

      // Calculate date for this day
      const dayDate = new Date(currentWeekStart);
      dayDate.setDate(dayDate.getDate() + dayIndex);
      const dayDateStr = dayDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });

      // Day header with background rectangle (only on first page)
      if (pageNum === 1) {
        doc.setFillColor(240, 240, 240);
        doc.rect(xPos, dayY - 3, columnWidth, 4, "F");
        doc.setFontSize(11); // Between original (10) and current (12)
        doc.setFont("helvetica", "bold");
        // Display day name and date
        const dayHeaderText = `${day} ${dayDateStr}`;
        doc.text(dayHeaderText, xPos + columnPadding, dayY - 0.5);
        dayY += 6; // Added space between day header and first property
      } else {
        // On subsequent pages, show continuation header
        doc.setFontSize(10); // Between original (9) and current (11)
        doc.setFont("helvetica", "bold");
        doc.text(`${day} (cont.)`, xPos + columnPadding, dayY - 0.5);
        dayY += 4;
      }

      // Get all properties and tasks for this day
      let allTasks = [];
      if (dayData && typeof dayData === "object") {
        const propertyNames = Object.keys(dayData).sort((a, b) => a.localeCompare(b));
        propertyNames.forEach((propertyName) => {
          const tasks = Array.isArray(dayData[propertyName]) ? dayData[propertyName] : [];
          tasks.forEach((task) => {
            allTasks.push({ ...task, propertyName });
          });
        });
      }

      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");

      if (allTasks.length === 0) {
        // Show "No tasks" even if empty (only on first page)
        if (pageNum === 1) {
          doc.setFont("helvetica", "italic");
          doc.setTextColor(150, 150, 150);
          doc.setFontSize(9); // Increased from 7
          doc.text("No tasks", xPos + columnPadding, dayY);
          doc.setTextColor(0, 0, 0);
          dayY += 4; // Keep line spacing the same
        }
      } else {
        // Show tasks grouped by property - filter out completed tasks
        const tasksByProp = {};
        allTasks.forEach((task) => {
          // Only include uncompleted tasks
          if (!task.done && !task.isCompletedEntry) {
            if (!tasksByProp[task.propertyName]) {
              tasksByProp[task.propertyName] = [];
            }
            tasksByProp[task.propertyName].push(task);
          }
        });

        const propertyNames = Object.keys(tasksByProp).sort();
        
        // Skip properties that were already fully rendered on previous pages
        const propertiesToRender = [];
        
        propertyNames.forEach((propertyName) => {
          const tasks = tasksByProp[propertyName];
          if (!tasks || tasks.length === 0) {
            return; // No tasks for this property, skip it
          }
          
          // Check if all tasks for this property were already rendered
          const allTasksRendered = tasks.every(task => renderedTaskIds[task.id] === true);
          
          if (allTasksRendered) {
            return; // Skip this property - all tasks were already rendered on a previous page
          }
          
          // This property needs to be rendered (either not started or partially rendered)
          propertiesToRender.push(propertyName);
        });
        
        propertiesToRender.forEach((propertyName) => {
          const tasks = tasksByProp[propertyName];
          
          // Filter out tasks that were already rendered
          const tasksToRender = tasks.filter(task => !renderedTaskIds[task.id]);
          
          // If no tasks to render for this property, skip it
          if (tasksToRender.length === 0) {
            return;
          }
          
          // Check if we have space for this property
          if (dayY > startY + maxContentHeight - bottomMargin) {
            hasMoreContent = true;
            return;
          }

          // Add spacing before property (except first)
          if (dayY > startY + 4) {
            dayY += 1; // Keep line spacing the same
          }

          // Property name with Key # - strictly constrain width with larger safety margin
          doc.setFontSize(10); // Between original (9) and current (11)
          doc.setFont("helvetica", "bold");
          // Leave larger safety margin on each side to prevent overlap
          const maxTextWidth = Math.max(8, columnWidth - (columnPadding * 2) - 6);
          const propId = propertyNameToIdMap[propertyName];
          const keyValue = propId ? (propertyKeyMap[propId] || propertyKeyMap[String(propId)]) : null;
          const propertyDisplayName = keyValue ? `${propertyName} (${keyValue})` : propertyName;
          const propLines = doc.splitTextToSize(cleanTextForPDF(propertyDisplayName), maxTextWidth);
          
          // Check if property name fits
          if (dayY + (propLines.length * 4) > startY + maxContentHeight - bottomMargin) {
            hasMoreContent = true;
            return;
          }
          
          propLines.forEach((line) => {
            if (dayY > startY + maxContentHeight - bottomMargin) return;
            // Ensure text doesn't exceed column boundary
            const textX = xPos + columnPadding + 1;
            if (textX + doc.getTextWidth(line) <= xPos + columnWidth - columnPadding - 1) {
              doc.text(line, textX, dayY);
            }
            dayY += 4; // Reduced from 7
          });
          dayY += 1; // Reduced from 2

          // Tasks for this property
          doc.setFontSize(9); // Increased from 7
          doc.setFont("helvetica", "normal");
          
          for (const task of tasksToRender) {
            // Check if we have enough space before rendering task
            if (dayY > startY + maxContentHeight - bottomMargin) {
              hasMoreContent = true;
              break;
            }

            // Use helper function to render task with subtasks
            const newYPos = renderTaskWithSubtasksWeekly(
              doc,
              task,
              xPos,
              dayY,
              columnWidth,
              columnPadding,
              startY,
              maxContentHeight,
              4, // lineHeight
              0  // indentLevel (0 for main tasks)
            );
            
            if (newYPos !== null) {
              dayY = newYPos;
              // Mark this task as rendered
              renderedTaskIds[task.id] = true;
            } else {
              // Content doesn't fit
              hasMoreContent = true;
              break;
            }
            
            dayY += 0; // No extra space between tasks - match property spacing
          }
          
          // Check if this property was fully rendered (all tasks fit)
          const allTasksRendered = tasks.every(task => renderedTaskIds[task.id] === true);
          if (allTasksRendered && !hasMoreContent) {
            renderedPropertyCount++;
          }
          
          dayY += 2; // Reduced from 4 - Extra spacing between properties
        });
      }

      return { dayY, hasMoreContent, renderedPropertyCount, renderedTaskIds };
    };

    // Render pages until all content is shown
    let currentPage = 1;
    let allDaysComplete = false;
    const dayRenderedTaskIds = {}; // Track which task IDs have been rendered for each day

    while (!allDaysComplete) {
      // Draw header on each page
      doc.setFontSize(15); // Increased from 12
      doc.setFont("helvetica", "bold");
      const pageHeader = currentPage === 1 
        ? `Tasks - Week of ${formatWeekRange(currentWeekStart)}`
        : `Tasks - Week of ${formatWeekRange(currentWeekStart)} (Page ${currentPage})`;
      doc.text(pageHeader, margin, 10); // Added top padding

      // Person filter info - moved to bottom right corner
      if (selectedPerson !== "all") {
        const personName = availablePeople.find(p => p.id === selectedPerson)?.name || selectedPerson;
        const filterText = `Filtered by: ${cleanTextForPDF(personName)}`;
        doc.setFontSize(10); // Slightly smaller for footer
        doc.setFont("helvetica", "normal");
        // Calculate text width and position at bottom right
        const textWidth = doc.getTextWidth(filterText);
        const xPos = pageWidth - margin - textWidth; // Right-aligned
        const yPos = pageHeight - 8; // 8mm from bottom
        doc.text(filterText, xPos, yPos);
      }

      // Draw all 7 days side by side on this page
      allDaysComplete = true;
      dayOrder.forEach((day, dayIndex) => {
        const xPos = margin + (dayIndex * (columnWidth + columnSpacing));
        
        // Get rendered task IDs for this day (which tasks were already rendered)
        if (!dayRenderedTaskIds[day]) {
          dayRenderedTaskIds[day] = {};
        }
        
        // Pass the rendered task IDs object - it will be modified in place
        const currentRenderedTaskIds = dayRenderedTaskIds[day];
        
        // Render day content - this will mark tasks as rendered in currentRenderedTaskIds
        const result = renderDayContent(day, dayIndex, xPos, startY, currentPage, currentRenderedTaskIds);
        
        // Track if this day has more content
        if (result.hasMoreContent) {
          allDaysComplete = false;
          // The currentRenderedTaskIds object was modified in place, so dayRenderedTaskIds[day] is already updated
        } else {
          // This day is complete, we can keep the rendered task IDs (they won't be used anyway)
        }

        // Draw vertical line between days (lighter, thinner)
        if (dayIndex < 6) {
          doc.setDrawColor(220, 220, 220);
          doc.setLineWidth(0.2);
          doc.line(xPos + columnWidth, startY - 7, xPos + columnWidth, pageHeight - 15);
          doc.setLineWidth(0.5); // Reset line width
        }
      });

      // If there's more content, create a new page
      if (!allDaysComplete) {
        currentPage++;
        doc.addPage();
      }
    }
  }

  // Generate filename
  const dateStr = viewType === "daily" 
    ? formatDate(currentDate).replace(/\//g, "-")
    : `Week-${formatWeekRange(currentWeekStart).replace(/\s/g, "-").replace(/,/g, "")}`;
  const filename = `Tasks-${dateStr}.pdf`;

  doc.save(filename);
}

